/* eslint-disable jsx-a11y/media-has-caption */
/* eslint-disable no-underscore-dangle */
import * as React from 'react';
import { Row, Col, Image, Tooltip, Modal, Carousel, Typography } from 'antd';
import {
  LeftCircleOutlined,
  RightCircleOutlined,
  WarningFilled,
} from '@ant-design/icons';
import { formatDate, formatDateTime } from '../../../utils/utils';
import { getConsignmentStatusPrettyName } from '../../../utils/consignmentUtils';
import moment from 'moment';
import withStyles from 'react-jss';
import { useTranslation } from 'react-i18next';

const { PreviewGroup } = Image;

const { useState } = React;
const { Paragraph } = Typography;

const styles = (theme) => ({
  container: {},
  title: {
    height: '48px',
    padding: '0 40px',
    backgroundColor: '#fcfcfc',
    fontSize: '16px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    color: '#424242',
  },
  rowContainer: {
    padding: '0px 10px 0px 40px',
    marginBottom: '20px',
  },
  hintHeader: {
    fontSize: '12px',
    color: '#595959',
    fontWeight: 400,
    margin: '10px 0px 10px 0',
  },
  content: {
    fontSize: '14px',
    color: '#262626',
    fontWeight: 500,
    wordBreak: 'break-word',
    textTransform: 'capitalize',
  },
  column: {
    margin: '10px 0px',
  },
  btn: {
    color: 'blue',
    cursor: 'default',
  },
  modalContainer: {
    padding: '1rem 0',
    paddingBottom: '3rem',
  },
  carouselContainer: {
    padding: '1.5rem',
    maxWidth: '100%',
    '& span': {
      color: '#222021 !important',
    },
    '& span > svg': {
      width: '120%',
      height: '120%',
    },
  },
});

interface DetailsCardProps {
  title: string;
  data: any;
  columns: any;
  classes: any;
  getCustomDetailsCard: any;
}

const DetailsCard = (props: DetailsCardProps) => {
  const { title, data, columns, classes, getCustomDetailsCard } = props;
  const [visible, setVisible] = React.useState(false);
  const [galleryData, setGalleryData] = useState([null]);
  const [isImageVideoModalVisible, setIsImageVideoModalVisible] = useState(false);
  const [imageVideoGalleryData, setImageVideoGalleryData] = useState({
    images: [],
    videos: [],
  });
  const { t } = useTranslation();

  const toggleGalleryWithData = (data = [null]) => {
    setVisible(!visible);
    setGalleryData(data);
  };

  const renderGallery = (data) => {
    if ((Array.isArray(data) && !data.length) || (!Array.isArray(data) && !data)) {
      return 'Not Available';
    }
    return (
      <>
        <div
          onClick={() => {
            toggleGalleryWithData(data);
          }}
          className={classes.btn}
        >
          {t('View')}
        </div>
      </>
    );
  };

  const toggleImageVideoGallery = (images, videos) => {
    setIsImageVideoModalVisible(!isImageVideoModalVisible);
    setImageVideoGalleryData({
      images,
      videos,
    });
  };

  const renderETA = (startTime, endTime) => {
    return startTime && endTime
      ? `${moment(startTime).format('Do MMMM')},
          ${moment(startTime).format('h A')} -
          ${moment(endTime).format('h A')}`
      : 'Not Available';
  };

  const renderBooleanData = (column, data) => {
    switch (column.id) {
      case 'is_cnv_done':
        return data[column.id] ? 'Verified' : 'Not Verified';
      default:
        return data[column?.id] ? 'Yes' : 'No';
    }
  };

  const renderImageVideoGallery = (imageArray, videoArray) => {
    return (
      <>
        <div
          onClick={() => {
            toggleImageVideoGallery(imageArray, videoArray);
          }}
          className={classes.btn}
        >
          {t('View')}
        </div>
      </>
    );
  };

  const renderEwayBillNo = (ewayBill, ewayBillList) => {
    if (ewayBill) {
      try {
        const wayBillObject = JSON.parse(ewayBill.trim());
        return <span>{wayBillObject['ewbNumber']?.toString()}</span>;
      } catch (err) {
        return 'Not Available';
      }
    }
    return 'Not Available';
  };

  const renderEwayBillValidity = (ewayBill) => {
    if (ewayBill) {
      try {
        const wayBillObject = JSON.parse(ewayBill);
        return formatDate(wayBillObject['validTill'], 'Not Available');
      } catch (err) {
        return 'Not Available';
      }
    }
    return 'Not Available';
  };

  const renderArrayData = (column, data) => {
    switch (column.key) {
      case '{current_delivery_address_line_1, current_delivery_address_line_2}':
      case '{destination_address_line_1, destination_address_line_2}':
      case '{sender_address_line_1, sender_address_line_2}':
        return `${data[column.id[0]] ? data[column.id[0]] : ' - '}
         ${data[column.id[1]] ? data[column.id[1]] : ''}`;
      case '{manifest_number, manifest_time}':
        return `${data.manifest_number ? data.manifest_number : 'Not Available'}
                      ${data.manifest_time ? data.manifest_time : ''}`;
      case '{start_time, end_time}':
        return renderETA(data[column.id[0]], data[column.id[1]]);
      case 'eway_bill':
        return renderEwayBillNo(data[column.id], data['eway_bill_list']);
      case 'eway_bill_expiry_date':
        return renderEwayBillValidity(data[column.id]);
      case 'quality_check_image_list':
        return renderGallery(data[column.id]);
      case '{height, width, length}':
        return column.id.length === 3 &&
          data[column.id[0]] &&
          data[column.id[1]] &&
          data[column.id[2]]
          ? `${data[column.id[0]]}*${data[column.id[1]]}*${data[column.id[2]]}`
          : 'Not Available';
      case 'attempt_proof':
        if (
          data['status'] === 'attempted' &&
          (data[column.id[0]]?.length > 0 || data[column.id[1]]?.length > 0)
        ) {
          const cnImages: string[] = [];
          data[column.id[0]]?.map((image) => {
            if (image) {
              cnImages.push(String(image.url));
            }
            return null;
          });
          const cnVideos: string[] = [];
          data[column.id[1]]?.map((video) => {
            if (video) {
              cnVideos.push(String(video.url));
            }
            return null;
          });
          return renderImageVideoGallery(cnImages, cnVideos);
        }
        if (data['status'] === 'attempted' && data[column.id[2]]) {
          return renderGallery(data[column.id[2]]);
        }
        return 'Not Available';
      case 'poc_image':
        if (
          (data['status'] === 'delivered' || data['status'] === 'rto_delivered') &&
          (data[column.id[0]]?.length > 0 || data[column.id[1]]?.length > 0)
        ) {
          const pieceImagesToShow: string[] = [];
          const pieceVideosToShow: string[] = [];

          data['pieces_detail']?.map((piece) => {
            // Piece level images/videos
            const imageUrlListPiece = piece?._internal?.image_url_list?.map((image) => {
              if (image) {
                pieceImagesToShow.push(String(image));
              }
              return null;
            });
            const videoUrlListPiece = piece?._internal?.video_url_list?.map((video) => {
              if (video) {
                pieceVideosToShow.push(String(video));
              }
              return null;
            });
            return null;
          });
          const concatImages = () => {
            const pieceImages = pieceImagesToShow || [];
            const cnImages: string[] = [];
            data[column.id[0]]?.map((image) => {
              if (image) {
                cnImages.push(String(image.url));
              }
              return null;
            });
            return [...cnImages, ...pieceImages];
          };
          const concatVideos = () => {
            const pieceVideos = pieceVideosToShow || [];
            const cnVideos: string[] = [];
            data[column.id[1]]?.map((video) => {
              if (video) {
                cnVideos.push(String(video.url));
              }
              return null;
            });
            return [...cnVideos, ...pieceVideos];
          };
          return renderImageVideoGallery(concatImages(), concatVideos());
        }
        if (
          (data['status'] === 'delivered' || data['status'] === 'rto_delivered') &&
          data[column.id[2]]
        ) {
          return renderGallery(data[column.id[2]]);
        }
        return 'Not Available';
      case 'pop_image':
        return renderGallery(data[column.id[0]] || data[column.id[1]]);
      default:
        return data[column.id] ? data[column.id] : 'Not Available';
    }
  };

  const renderEWBPartBGenerationTime = (epochTime) => {
    if (epochTime) {
      try {
        return formatDate(epochTime, 'Not Available');
      } catch (err) {
        return 'Not Available';
      }
    }
    return 'Not Available';
  };

  const renderAmountCollected = (codAmountCollected) => {
    let sum = 0;
    codAmountCollected?.map((ele) => {
      sum += ele.cod_amount;
      return null;
    });
    return sum || 'Not Available';
  };

  const renderTooltipParagraph = (textToDisplay) => {
    return (
      <Tooltip
        overlayInnerStyle={{
          backgroundColor: '#fcfcfc',
          color: '#424242',
          border: '0.5px solid',
        }}
        autoAdjustOverflow
        title={textToDisplay}
      >
        <Paragraph
          ellipsis={{
            rows: 3,
          }}
        >
          {textToDisplay || 'Not Available'}
        </Paragraph>
      </Tooltip>
    );
  };

  const getDataToRender = (column, data) => {
    if (column.type === 'boolean') {
      return renderBooleanData(column, data);
    }
    if (column.type === 'Array') {
      return renderArrayData(column, data);
    }
    if (column.type === 'date') {
      switch (column.id) {
        case 'hub_arrival_time':
        case 'expected_delivery_date':
        case 'delivery_completion_time':
        case 'delivery_time_slot_start':
        case 'delivery_time_slot_end':
        case 'pickup_time_slot_start':
        case 'pickup_time_slot_end':
          return formatDateTime(parseInt(data[column.id], 10));
        default:
          return formatDate(data[column.id], 'Not Available');
      }
    }
    switch (column.id) {
      case 'movement_type':
      case 'consignment_type':
        return data[column.id] !== null && data[column.id] !== undefined
          ? data[column.id]
          : 'Forward';
      case 'status':
        return (
          <>
            {getConsignmentStatusPrettyName(data[column.id])}
            {data.is_partial_delivery && (
              <Tooltip title="Partially Delivered">
                <WarningFilled style={{ fontSize: 18, color: '#EC544A', marginLeft: 8 }} />
              </Tooltip>
            )}
          </>
        );
      case 'cod_amount':
        return data[column.id] || 0;
      case 'cod_amount_collected':
        return renderAmountCollected(data[column.id]);
      case 'signature_image':
      case 'pop_signature_image':
      case 'delivery_kyc_image':
      case 'reference_image_url':
      case 'card_transaction_image':
        return renderGallery(data[column.id]);

      case 'delivery_time_slot_start':
      case 'delivery_time_slot_end':
      case 'pickup_time_slot_start':
      case 'pickup_time_slot_end':
        return data[column.id]
          ? moment(parseInt(data[column.id], 10)).format('HH:mm')
          : 'Not Available';
      case 'execution_status':
        return null;
      case 'worker_comment':
        return renderTooltipParagraph(data[column.id]);
      case 'ewb_partb_generation_time':
        return renderEWBPartBGenerationTime(data[column.id]);
      case 'destination_lat':
        if (data.pickup_completed_event_details?.lat) {
          return data.pickup_completed_event_details?.lat;
        }
        return Array.isArray(data.delivery_location) && data.delivery_location?.length === 2
          ? data.delivery_location[0]
          : 'Not Available';
      case 'destination_lon':
        if (data.pickup_completed_event_details?.lng) {
          return data.pickup_completed_event_details?.lng;
        }
        return Array.isArray(data.delivery_location) && data.delivery_location?.length === 2
          ? data.delivery_location[1]
          : 'Not Available';
      case 'origin_lat':
        return Array.isArray(data.origin_location) && data.origin_location?.length === 2
          ? data.origin_location[0]
          : 'Not Available';
      case 'origin_lon':
        return Array.isArray(data.origin_location) && data.origin_location?.length === 2
          ? data.origin_location[1]
          : 'Not Available';
      default:
        return data[column?.id] ? data[column?.id] : 'Not Available';
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.title}>{title}</div>
      <div className={classes.rowContainer}>
        <Row gutter={40}>
          {columns?.map((column, index) => {
            if (getCustomDetailsCard) {
              const dataToRender = getCustomDetailsCard(column, data);
              return dataToRender?.hint ? (
                <Col span={column.size ? 6 * column.size : 6} key={column.id}>
                  <div className={classes.column}>
                    <div className={classes.hintHeader}>{t(dataToRender.hint)}</div>
                    <div className={classes.content}>{dataToRender.content ?? '-'}</div>
                  </div>
                </Col>
              ) : null;
            }
            return (
              column && (
                <Col span={column.size ? 6 * column.size : 6} key={column.id}>
                  <div className={classes.column}>
                    <div className={classes.hintHeader}>{t(column.pretty_name)}</div>
                    <div className={classes.content}>{getDataToRender(column, data)}</div>
                  </div>
                </Col>
              )
            );
          })}
        </Row>
        <div style={{ display: 'none' }}>
          <PreviewGroup
            preview={{
              visible,
              onVisibleChange: (vis) => toggleGalleryWithData(),
            }}
          >
            {Array.isArray(galleryData) ? (
              galleryData?.map((url) => <Image src={String(url)} placeholder />)
            ) : (
              <Image src={galleryData} placeholder />
            )}
          </PreviewGroup>
        </div>
      </div>
      {isImageVideoModalVisible && (
        <Modal
          className={classes.modalContainer}
          visible={isImageVideoModalVisible}
          onCancel={() => setIsImageVideoModalVisible(false)}
          footer={null}
        >
          <Carousel
            className={classes.carouselContainer}
            prevArrow={<LeftCircleOutlined />}
            nextArrow={<RightCircleOutlined />}
            arrows
          >
            {imageVideoGalleryData?.images?.map((data) => (
              <div key={data}>
                <img src={data} alt="" />
              </div>
            ))}
            {imageVideoGalleryData?.videos?.map((data) => (
              <div key={data} style={{ maxWidth: '100%' }}>
                <video controls style={{ maxWidth: '100%' }}>
                  <source src={data} type="video/mp4" />
                  {t('Your browser does not support the video tag.')}
                </video>
              </div>
            ))}
          </Carousel>
        </Modal>
      )}
    </div>
  );
};

const DetailsCardStyled = withStyles(styles, { injectTheme: true })(DetailsCard);

export default DetailsCardStyled;
